import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { computed } from '@preact/signals-core';
import { LiveData, Service } from '@toeverything/infra';

// import { combineLatest, map } from 'rxjs';
import type { FeatureFlagService } from '../../feature-flag';
import type { GlobalStateService } from '../../storage';

const AI_NETWORK_SEARCH_KEY = 'AINetworkSearch';

/**
 * AINetworkSearchService - Manages Web Search toggle state
 *
 * Auto-enable behavior:
 * - If visible (feature flag enabled) AND user hasn't explicitly set preference
 * - Default to enabled (true) instead of disabled
 * - This ensures users with web search capability have it on by default
 */
export class AINetworkSearchService extends Service {
  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly featureFlagService: FeatureFlagService
  ) {
    super();

    // Create raw enabled signal from user preference
    const { signal: rawEnabled, cleanup: enabledCleanup } =
      createSignalFromObservable<boolean | undefined>(
        this._rawEnabled$,
        undefined
      );
    this._rawEnabled = rawEnabled;
    this.disposables.push(enabledCleanup);

    const { signal: visible, cleanup: visibleCleanup } =
      createSignalFromObservable<boolean | undefined>(
        this._visible$,
        undefined
      );
    this.visible = visible;
    this.disposables.push(visibleCleanup);

    // Computed enabled: default to true if visible and user hasn't set preference
    this.enabled = computed(() => {
      const userPref = this._rawEnabled.value;
      const isVisible = this.visible.value;

      // If user has explicitly set a preference, use it
      if (userPref !== undefined) {
        return userPref;
      }

      // Auto-enable: if feature is visible, default to ON
      // This ensures users with web search capability have it enabled by default
      return isVisible === true ? true : undefined;
    });
  }

  visible: Signal<boolean | undefined>;

  enabled: Signal<boolean | undefined>;

  private readonly _rawEnabled: Signal<boolean | undefined>;

  private readonly _visible$ =
    this.featureFlagService.flags.enable_ai_network_search.$;

  private readonly _rawEnabled$ = LiveData.from(
    this.globalStateService.globalState.watch<boolean>(AI_NETWORK_SEARCH_KEY),
    undefined
  );

  setEnabled = (enabled: boolean) => {
    this.globalStateService.globalState.set(AI_NETWORK_SEARCH_KEY, enabled);
  };
}
